import {
	LuIcon,
	Menu,
	type MenuRootProps,
	type MenuVariantProps,
	radiusCva,
	Spinner,
	Text,
	useProvider,
} from "@overmap-ai/blocks"
import { type GeoImage } from "@overmap-ai/common"
import { selectGeoImageById } from "@overmap-ai/core"
import { ImageFile } from "@overmap-ai/forms"
import { memo } from "react"

import { useFetchFileFromFileModel } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector } from "../../store"
import { GeoImageIndexLabel } from "../IndexLabels/GeoImageIndex"

interface GeoImageViewerPopoverProps extends MenuRootProps, MenuVariantProps {
	geoImageId: GeoImage["uuid"]
}

export const GeoImageViewerPopover = memo<GeoImageViewerPopoverProps>((props) => {
	const providerContext = useProvider()
	const { children, geoImageId, size, radius = providerContext.radius, variant, accentColor, ...rest } = props

	const geoImage = useAppSelector(selectGeoImageById(geoImageId))

	if (!geoImage) {
		throw new Error(`GeoImage with uuid ${geoImageId} does not exist in the store`)
	}

	const [file, error] = useFetchFileFromFileModel(geoImage, sdk.geoImages)

	return (
		<Menu.Root {...rest}>
			{children}
			<Menu.Content size={size} radius={radius} variant={variant} accentColor={accentColor}>
				<div className="relative size-max max-h-[300px] min-h-[200px] min-w-[200px] max-w-[300px] overflow-hidden p-2">
					{!error && file ? (
						<>
							<ImageFile className={radiusCva({ maxLarge: true, radius })} file={file} />
							<div className="bg-(--base-2) absolute bottom-0 left-0 right-0 flex w-full flex-col p-2">
								<div className="flex items-center gap-1">
									<GeoImageIndexLabel
										geoImageId={geoImage.uuid}
										className="text-(--base-a11) text-sm"
									/>
									<Text size="sm" className="truncate">
										{geoImage.title}
									</Text>
								</div>
								{geoImage.original_date && (
									<Text accentColor="base" size="xs">
										{new Date(geoImage.original_date).toLocaleString(undefined, {
											month: "long",
											day: "numeric",
											year: "numeric",
											hour: "numeric",
											minute: "numeric",
										})}
									</Text>
								)}
							</div>
						</>
					) : !error ? (
						<div className="flex h-[200px] w-[200px] items-center justify-center">
							<Spinner />
						</div>
					) : (
						<div className="flex h-[200px] w-[200px] items-center justify-center">
							<LuIcon icon={modelIconMapping.alertCircle} />
							<Text accentColor="base" size="xs">
								Failed to load image
							</Text>
						</div>
					)}
				</div>
			</Menu.Content>
		</Menu.Root>
	)
})
GeoImageViewerPopover.displayName = "GeoImageViewerPopover"
